import { isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import { memo, useEffect } from 'react';

import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus.tsx';
import { TakeProfitOrderCreationTile } from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile';
import { useTakeProfitTileId } from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext.tsx';
import TakeProfitOrderEditingTile from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderEditingTile/index.tsx';
import { TakeProfitOrderExecutedTile } from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderExecutedTile/index.tsx';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import { selectTakeProfitTile } from '@/store/orders/takeProfitOrder/selectors.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

export const TakeProfitOrderTile = memo(() => {
  const tileId = useTakeProfitTileId();

  const dispatch = useAppDispatch();
  const userPreferences = useAppSelector((state) => state.userPreferences);

  const { status, mode, errorCode } = useAppSelector((state) => {
    const tile = selectTakeProfitTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        status: 'creating',
      };
    }

    return {
      status: tile.properties.status.type,
      mode: tile.properties.mode,
      errorCode: tile.properties.status.type === 'failed' ? tile.properties.status.errorMessage : undefined,
    };
  }, deepEqual);

  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));

  useEffect(() => {
    if (tileWidth !== 1) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w: 1 }));
    }
  }, [tileId, tileWidth]);

  if (status === 'pending') {
    return <ExecutionPendingOrderStatus isEditing={mode === 'EDIT'} />;
  }

  if (status === 'failed') {
    const handleReset = () => {
      dispatch(ordersSlice.actions.resetOrder({ tileId, userPreferences }));
    };

    return (
      <ErrorTile
        tileId={tileId}
        onReset={handleReset}
        errorCode={errorCode}
        quoteId={tileId}
        errorDescription={
          errorCode === 'order.timeout' ? 'order.timeout.error.message.description' : 'order.error.message.description'
        }
        errorTitle={errorCode === 'order.timeout' ? 'order.timeout.error.message.title' : 'order.error.message.title'}
      />
    );
  }

  if (mode === 'EDIT') {
    return <TakeProfitOrderEditingTile />;
  }

  if (status === 'creating') {
    return <TakeProfitOrderCreationTile />;
  }

  return <TakeProfitOrderExecutedTile />;
});
